import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddButtonInTopBar from "../components/AddButtonInTopBar";
import DataGrid from "../components/common/DataGrid";
import PageContent from "../components/pageContent";
import ProductBreadCrumb from "../components/products/ProductBreadCrumb";
import TopBarWithBreadCrumb from "../components/TopBarWithBreadCrumb";
import { checkCrud } from "../helpers/userRolesHelper";
import ReceiptPopup from "../popups/ReceiptPopup";
import { addNotification } from "../redux/actions";
import { apiDeleteReceipt, apiGetAllSpecialReceipts, apiGetReceiptById, apiGetReceipts } from "../services/ReceiptsService";
import { apiGetAllSellers } from "../services/SellersService";
import { useTranslation } from "react-i18next";
import CheckBox from "../components/common/CheckBox";

interface Props {
    contextLevel: string;
}

interface PopupReceiptDetail {
    visible: boolean,
    receiptDetail: any,
    id: any
}

interface RecepturyGridProps {
    onlySpecial: boolean;
    productPrefersLitres: boolean;
    selectedProductId: any;
    loading: boolean;
    rows: any[];
    canDelete: boolean;
    canUpdate: boolean;
    handleEditAction: any;
    onDeleteRow: any;
}

const RecepturyGrid = memo((props: RecepturyGridProps) => {
    const { t } = useTranslation(undefined, { keyPrefix: 'Receipts' });

    const amountColumnName = props.productPrefersLitres
        ? t('Fields.basicAmountInMiliGrams')
        : t('Fields.basicAmountInGrams');

    const gridCols = useMemo(() => props.onlySpecial ? [
        { name: t('Fields.designation'), dataField: "receiptDisplayName", sortable: true, filterable: true, filterType: "text", colorWidth: "220px" },
        { name: t('Fields.product'), dataField: "productName", sortable: true, filterable: true, filterType: "text" },
        { name: t('Fields.basis'), dataField: "basisName", sortable: true },
        { name: amountColumnName, dataField: "customAmount", sortable: true },
        { name: t('Fields.sampleCard'), dataField: "sampleCardName", sortable: true, filterable: true, filterType: "text" },
        { name: t('Fields.version'), dataField: "version", filterable: true },
        { name: t('Fields.special'), dataField: "sellerNamesDisplay", sortable: true, filterable: true, filterType: "text" },
        { name: t('Fields.pigmentsRatio'), dataField: "pigmentsRatio" },
        { name: t('Fields.noteInternal'), dataField: "noteInternal" },
        { name: t('Fields.noteExternal'), dataField: "noteExternal" }
    ] : [
        { name: t('Fields.product'), dataField: "productName" },
        { name: t('Fields.basis'), dataField: "basisName", sortable: true },
        { name: amountColumnName, dataField: "customAmount", sortable: true },
        { name: t('Fields.sampleCard'), dataField: "sampleCardName", sortable: true, filterable: true, filterType: "text" },
        { name: t('Fields.color'), dataField: "colorName", sortable: true, filterable: true, filterType: "text" },
        { name: t('Fields.version'), dataField: "version", filterable: true },
        { name: t('Fields.special'), dataField: "sellerNamesDisplay", sortable: true, filterable: true, filterType: "text" },
        { name: t('Fields.pigmentsRatio'), dataField: "pigmentsRatio" },
        { name: t('Fields.noteInternal'), dataField: "noteInternal" },
        { name: t('Fields.noteExternal'), dataField: "noteExternal" }
    ], [amountColumnName, props.onlySpecial, t]);

    return (
        <DataGrid
            key={`RecepturyGrid_Product_${props.onlySpecial ? 'special' : props.selectedProductId}`}
            loading={props.loading}
            rows={props.rows}
            showDeleteButton={props.canDelete}
            showEditButton={props.canUpdate}
            handleEditAction={props.handleEditAction}
            onDeleteRow={props.onDeleteRow}
            cols={gridCols}
        />
    );
}, (prev, next) => {
    return prev.onlySpecial === next.onlySpecial
        && prev.productPrefersLitres === next.productPrefersLitres
        && prev.selectedProductId === next.selectedProductId
        && prev.loading === next.loading
        && prev.rows === next.rows
        && prev.canDelete === next.canDelete
        && prev.canUpdate === next.canUpdate
        && prev.handleEditAction === next.handleEditAction
        && prev.onDeleteRow === next.onDeleteRow;
});

const entityName = 'receipt';

const RecepturyPage = (props: Props) => {

    const { t } = useTranslation(undefined, { keyPrefix: 'Receipts' });

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<{ rows: any[] }>({ rows: [] });
    const [onlySpecial, setOnlySpecial] = useState(false);
    const [allSellers, setAllSellers] = useState<any[]>([]);

    // poradove cislo posledniho pozadavku - odpovedi z drivejsich vyberu se zahazuji
    const requestIdRef = useRef(0);
    const sellersPromiseRef = useRef<Promise<any[]> | null>(null);

    const getSellers = useCallback(() => {
        if (!sellersPromiseRef.current) {
            sellersPromiseRef.current = apiGetAllSellers()
                .then((r) => r.data)
                .catch((err) => {
                    sellersPromiseRef.current = null;
                    throw err;
                });
        }
        return sellersPromiseRef.current;
    }, []);

    useEffect(() => {
        getSellers().then(setAllSellers).catch(() => { });
    }, [getSellers]);

    let initReceiptDetail: PopupReceiptDetail = {
        visible: false,
        receiptDetail: null,
        id: undefined,
    }

    const [receiptPopupData, setReceiptPopupData] = useState(initReceiptDetail);

    const productInfo = useSelector((state: any) => state.productInfo);
    const selectedProductId = productInfo?.productId;
    const dispatch = useDispatch();

    const [canCreate, setCanCreate] = useState(false);
    const [canRead, setCanRead] = useState(false);
    const [canUpdate, setCanUpdate] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    const userInfo = useSelector((state: any) => state.userInfo);

    const setRoles = () => {
        setCanCreate(checkCrud(entityName, 'create', userInfo));
        setCanRead(checkCrud(entityName, 'read', userInfo));
        setCanUpdate(checkCrud(entityName, 'update', userInfo));
        setCanDelete(checkCrud(entityName, 'delete', userInfo));
    }

    useEffect(() => {
        setRoles();
    }, [userInfo.UserRoles])

    const enrichRows = useCallback((rows: any[], sellers: any[]) => {
        return rows.map((row) => ({
            ...row,
            receiptDisplayName: row.name || row.colorName,
            sellerNamesDisplay: Array.isArray(row.sellerIds) && row.sellerIds.length > 0
                ? row.sellerIds
                    .map((id: any) => sellers.find((s) => s.id === id || s.id === Number(id)))
                    .filter(Boolean)
                    .map((s: any) => s.name)
                    .join(', ')
                : (row.sellerName ?? ''),
        }));
    }, []);

    const loadData = useCallback(() => {

        const requestId = ++requestIdRef.current;
        const isCurrent = () => requestId === requestIdRef.current;

        if (!onlySpecial && !selectedProductId) {
            setData({ rows: [] });
            setLoading(false);
            return;
        }

        setLoading(true);

        const receiptsPromise = onlySpecial
            ? apiGetAllSpecialReceipts()
            : apiGetReceipts(selectedProductId);

        Promise.all([receiptsPromise, getSellers()])
            .then(([responseData, sellers]) => {
                if (!isCurrent()) {
                    return;
                }
                setAllSellers(sellers);
                setData({ rows: enrichRows(responseData.data, sellers) });
                setLoading(false);
            })
            .catch(() => {
                if (isCurrent()) {
                    setLoading(false);
                }
            });
    }, [onlySpecial, selectedProductId, enrichRows, getSellers]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const closeReceiptPopup = useCallback(() => {
        setReceiptPopupData({ visible: false, id: undefined, receiptDetail: null });
    }, []);

    const handleEditAction = useCallback(async (id: any) => {
        let receiptDetail = await apiGetReceiptById(id);
        setReceiptPopupData({ visible: true, id: id, receiptDetail: receiptDetail.data });
    }, []);

    const handleDeleteRow = useCallback((row) => {
        apiDeleteReceipt(row.id)
            .then((responseData) => {
                dispatch(addNotification({ text: t('ReceiptHasBeenRemoved'), type: 'success' }));

                loadData();
            })
            .catch((err: any) => {
                dispatch(addNotification({ text: `${t('ReceiptCouldNotBeRemoved')} ${err.response.data}`, type: 'error' }));
            });
    }, [dispatch, loadData, t]);


    return (
        <>
            {receiptPopupData.visible && (
                <ReceiptPopup
                    isCustom={false}
                    id={receiptPopupData.id}
                    productPrefersLitres={productInfo.productPrefersLitres}
                    receiptDetail={receiptPopupData.receiptDetail}
                    refreshData={() => {
                        loadData();
                        closeReceiptPopup();
                    }
                    }
                    handleCancel={closeReceiptPopup}
                />
            )}

            <PageContent>
                <>
                    <div style={onlySpecial ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
                        <ProductBreadCrumb selectionOfProductIsRequired={true} />
                    </div>
                    <TopBarWithBreadCrumb
                        contextLevel={props.contextLevel}
                        pageLevel={t('Label')}
                        rightAreaComponent={
                            <AddButtonInTopBar
                                enabled={canCreate && !onlySpecial}
                                setPopupData={setReceiptPopupData}
                                text={t('CreateText')}
                            />
                        }
                    />

                    <CheckBox
                        id="onlySpecialReceipts"
                        label={t('OnlySpecial')}
                        paddingLeft={"15px"}
                        defaultValue={onlySpecial}
                        handleCheckedChange={(checked: boolean) => {
                            setOnlySpecial(checked);
                        }}
                    />

                    <RecepturyGrid
                        onlySpecial={onlySpecial}
                        productPrefersLitres={!!productInfo?.productPrefersLitres}
                        selectedProductId={selectedProductId}
                        loading={loading}
                        rows={data.rows}
                        canDelete={canDelete}
                        canUpdate={canUpdate}
                        handleEditAction={handleEditAction}
                        onDeleteRow={handleDeleteRow}
                    />
                </>
            </PageContent>
        </>
    );
};

export default RecepturyPage;
